import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Select,
  TextInput,
} from '@etherisc/ui-kit';
import { Clock, X, Check, AlertCircle } from 'lucide-react';

type Frequency = 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';

interface CronScheduleWidgetProps {
  value: string;
  onChange: (cron: string) => void;
  disabled?: boolean;
}

const FREQUENCY_OPTIONS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: String(i).padStart(2, '0'),
}));

const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => ({
  value: String(i),
  label: String(i).padStart(2, '0'),
}));

const DAY_OF_WEEK_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' },
];

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

const HOURLY_INTERVAL_OPTIONS = [
  { value: '1', label: 'Every hour' },
  { value: '2', label: 'Every 2 hours' },
  { value: '3', label: 'Every 3 hours' },
  { value: '4', label: 'Every 4 hours' },
  { value: '6', label: 'Every 6 hours' },
  { value: '8', label: 'Every 8 hours' },
  { value: '12', label: 'Every 12 hours' },
];

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function cronToHumanReadable(cron: string): string {
  if (!cron) return '';
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;

  const [min, hr, dom, , dow] = parts;
  const pad = (v: string) => v.padStart(2, '0');

  if (hr?.startsWith('*/') && dom === '*' && dow === '*') {
    const interval = hr.replace('*/', '');
    return interval === '1'
      ? `Every hour at :${pad(min ?? '0')}`
      : `Every ${interval} hours at :${pad(min ?? '0')}`;
  }

  if (dom === '*' && dow === '*' && !hr?.includes('/') && !hr?.includes(',')) {
    return `Daily at ${pad(hr ?? '0')}:${pad(min ?? '0')}`;
  }

  if (dom === '*' && dow !== '*' && !dow?.includes('/')) {
    const dayName = DAY_NAMES[Number(dow)] ?? dow;
    return `Weekly on ${dayName} at ${pad(hr ?? '0')}:${pad(min ?? '0')}`;
  }

  if (dom !== '*' && dow === '*' && !dom?.includes('/')) {
    const suffix = dom === '1' ? 'st' : dom === '2' ? 'nd' : dom === '3' ? 'rd' : 'th';
    return `Monthly on the ${dom}${suffix} at ${pad(hr ?? '0')}:${pad(min ?? '0')}`;
  }

  return cron;
}

function parseCronToState(cron: string): {
  frequency: Frequency;
  minute: string;
  hour: string;
  dayOfWeek: string;
  dayOfMonth: string;
  hourlyInterval: string;
  customCron: string;
} {
  const defaults = {
    frequency: 'daily' as Frequency,
    minute: '0',
    hour: '9',
    dayOfWeek: '1',
    dayOfMonth: '1',
    hourlyInterval: '1',
    customCron: cron || '0 9 * * *',
  };

  if (!cron) return defaults;

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return { ...defaults, frequency: 'custom', customCron: cron };

  const [min, hr, dom, , dow] = parts;

  // Hourly: */N or 0 */N * * *
  if (hr?.startsWith('*/') && dom === '*' && dow === '*') {
    return {
      ...defaults,
      frequency: 'hourly',
      minute: min ?? '0',
      hourlyInterval: hr.replace('*/', ''),
    };
  }

  // Daily: M H * * *
  if (dom === '*' && dow === '*' && !hr?.includes('/') && !hr?.includes(',')) {
    return { ...defaults, frequency: 'daily', minute: min ?? '0', hour: hr ?? '9' };
  }

  // Weekly: M H * * D
  if (dom === '*' && dow !== '*' && !dow?.includes('/')) {
    return { ...defaults, frequency: 'weekly', minute: min ?? '0', hour: hr ?? '9', dayOfWeek: dow ?? '1' };
  }

  // Monthly: M H D * *
  if (dom !== '*' && dow === '*' && !dom?.includes('/')) {
    return { ...defaults, frequency: 'monthly', minute: min ?? '0', hour: hr ?? '9', dayOfMonth: dom ?? '1' };
  }

  return { ...defaults, frequency: 'custom', customCron: cron };
}

function buildCron(state: {
  frequency: Frequency;
  minute: string;
  hour: string;
  dayOfWeek: string;
  dayOfMonth: string;
  hourlyInterval: string;
  customCron: string;
}): string {
  switch (state.frequency) {
    case 'hourly':
      return `${state.minute} */${state.hourlyInterval} * * *`;
    case 'daily':
      return `${state.minute} ${state.hour} * * *`;
    case 'weekly':
      return `${state.minute} ${state.hour} * * ${state.dayOfWeek}`;
    case 'monthly':
      return `${state.minute} ${state.hour} ${state.dayOfMonth} * *`;
    case 'custom':
      return state.customCron;
  }
}

function validateCron(cron: string): string | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return 'Cron expression must have exactly 5 fields';

  const [min, hr, dom, mon, dow] = parts;

  const isValidField = (val: string, min: number, max: number): boolean => {
    if (val === '*') return true;
    if (val?.startsWith('*/')) {
      const step = parseInt(val.substring(2));
      return !isNaN(step) && step >= 1 && step <= max;
    }
    // Comma-separated values
    return val?.split(',').every(v => {
      const n = parseInt(v);
      return !isNaN(n) && n >= min && n <= max;
    }) ?? false;
  };

  if (!isValidField(min!, 0, 59)) return 'Invalid minute field (0-59)';
  if (!isValidField(hr!, 0, 23)) return 'Invalid hour field (0-23)';
  if (!isValidField(dom!, 1, 31)) return 'Invalid day-of-month field (1-31)';
  if (!isValidField(mon!, 1, 12)) return 'Invalid month field (1-12)';
  if (!isValidField(dow!, 0, 7)) return 'Invalid day-of-week field (0-7)';

  return null;
}

function describeSchedule(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;

  const [min, hr, dom, , dow] = parts;
  const pad = (s: string) => s?.padStart(2, '0') ?? '00';

  if (hr?.startsWith('*/') && dom === '*' && dow === '*') {
    const interval = hr.replace('*/', '');
    return `Every ${interval} hour(s) at minute ${pad(min ?? '0')}`;
  }
  if (dom === '*' && dow === '*') return `Daily at ${pad(hr ?? '0')}:${pad(min ?? '0')}`;
  if (dom === '*' && dow !== '*') {
    const dayName = DAY_OF_WEEK_OPTIONS.find(d => d.value === dow)?.label ?? dow;
    return `Weekly on ${dayName} at ${pad(hr ?? '0')}:${pad(min ?? '0')}`;
  }
  if (dom !== '*' && dow === '*') return `Monthly on day ${dom} at ${pad(hr ?? '0')}:${pad(min ?? '0')}`;

  return cron;
}

export function CronScheduleWidget({ value, onChange, disabled }: CronScheduleWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState(() => parseCronToState(value));

  useEffect(() => {
    setState(parseCronToState(value));
  }, [value]);

  const currentCron = useMemo(() => buildCron(state), [state]);
  const validationError = useMemo(() => validateCron(currentCron), [currentCron]);

  const handleFieldChange = useCallback(<K extends keyof typeof state>(field: K, val: typeof state[K]) => {
    setState(prev => ({ ...prev, [field]: val }));
  }, []);

  const handleSave = useCallback(() => {
    if (!validationError) {
      onChange(currentCron);
      setIsOpen(false);
    }
  }, [currentCron, validationError, onChange]);

  const handleCancel = useCallback(() => {
    setState(parseCronToState(value));
    setIsOpen(false);
  }, [value]);

  if (!isOpen) {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(true)}
          disabled={disabled}
        >
          <Clock size={14} className="mr-1" />
          {value ? 'Edit Schedule' : 'Configure Schedule'}
        </Button>
        {value && (
          <span className="text-sm text-gray-600">
            {describeSchedule(value)}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-full max-w-md mx-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Configure Schedule</CardTitle>
            <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Frequency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
              <Select
                value={state.frequency}
                onValueChange={(v) => handleFieldChange('frequency', v as Frequency)}
                options={FREQUENCY_OPTIONS}
              />
            </div>

            {/* Time (not shown for custom) */}
            {state.frequency !== 'custom' && (
              <div className="grid grid-cols-2 gap-3">
                {state.frequency !== 'hourly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hour</label>
                    <Select
                      value={state.hour}
                      onValueChange={(v) => handleFieldChange('hour', v)}
                      options={HOUR_OPTIONS}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minute</label>
                  <Select
                    value={state.minute}
                    onValueChange={(v) => handleFieldChange('minute', v)}
                    options={MINUTE_OPTIONS}
                  />
                </div>
              </div>
            )}

            {/* Conditional 4th field */}
            {state.frequency === 'hourly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Interval</label>
                <Select
                  value={state.hourlyInterval}
                  onValueChange={(v) => handleFieldChange('hourlyInterval', v)}
                  options={HOURLY_INTERVAL_OPTIONS}
                />
              </div>
            )}

            {state.frequency === 'weekly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
                <Select
                  value={state.dayOfWeek}
                  onValueChange={(v) => handleFieldChange('dayOfWeek', v)}
                  options={DAY_OF_WEEK_OPTIONS}
                />
              </div>
            )}

            {state.frequency === 'monthly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day of Month</label>
                <Select
                  value={state.dayOfMonth}
                  onValueChange={(v) => handleFieldChange('dayOfMonth', v)}
                  options={DAY_OF_MONTH_OPTIONS}
                />
              </div>
            )}

            {state.frequency === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cron Expression</label>
                <TextInput
                  value={state.customCron}
                  onChange={(e) => handleFieldChange('customCron', e.target.value)}
                  placeholder="0 0 * * *"
                />
                <p className="text-xs text-gray-500 mt-1">Format: minute hour day-of-month month day-of-week</p>
              </div>
            )}

            {/* Preview */}
            <div className="bg-gray-50 rounded p-3 space-y-1">
              <div className="text-sm font-medium text-gray-700">Preview</div>
              <div className="text-sm font-mono text-gray-800">{currentCron}</div>
              <div className="text-xs text-gray-600">{describeSchedule(currentCron)}</div>
              {validationError && (
                <div className="flex items-center gap-1 text-xs text-red-600 mt-1">
                  <AlertCircle size={12} />
                  {validationError}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!!validationError}
              >
                <Check size={14} className="mr-1" />
                Save
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
